import {
  type FocusEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react'

const ITEMS = 'button:not([disabled]), select:not([disabled])'

interface RovingToolbarProps<T extends HTMLElement> {
  ref: (element: T | null) => void
  onKeyDown: (event: KeyboardEvent<T>) => void
  onFocus: (event: FocusEvent<T>) => void
}

// A toolbar that takes one Tab stop: the last focused item (the first, to
// begin with) is tabbable, the rest are reached with ←/→, Home and End.
export function useRovingToolbar<
  T extends HTMLElement,
>(): RovingToolbarProps<T> {
  const root = useRef<T | null>(null)
  const active = useRef(0)

  const items = useCallback(
    (): HTMLElement[] =>
      root.current
        ? [...root.current.querySelectorAll<HTMLElement>(ITEMS)]
        : [],
    []
  )
  const settle = useCallback((): void => {
    const all = items()
    const index = Math.min(active.current, Math.max(all.length - 1, 0))
    all.forEach((item, at) => {
      item.tabIndex = at === index ? 0 : -1
    })
  }, [items])

  // The items come and go with the node's state: settle after every render.
  useEffect(() => {
    settle()
  })

  return {
    ref: (element) => {
      root.current = element
      settle()
    },
    onFocus: (event) => {
      const at = items().indexOf(event.target as HTMLElement)
      if (at !== -1 && at !== active.current) {
        active.current = at
        settle()
      }
    },
    onKeyDown: (event) => {
      const all = items()
      if (!all.length) {
        return
      }
      const step = new Map<string, number>([
        ['ArrowRight', active.current + 1],
        ['ArrowLeft', active.current - 1],
        ['Home', 0],
        ['End', all.length - 1],
      ])
      const next = step.get(event.key)
      if (next === undefined) {
        return
      }
      event.preventDefault()
      active.current = (next + all.length) % all.length
      settle()
      all[active.current]?.focus()
    },
  }
}
